import { useState } from 'react';
import { Field, Deposit, Section, Horizon, Bed } from '../types/types';

export const generateId = () => Math.random().toString(36).substr(2, 9);

export const useDuplicateEntities = () => {
  const [duplicateName, setDuplicateName] = useState<string>('');
  const [deepCopy, setDeepCopy] = useState<boolean>(false);
  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    type: '' as 'deposit' | 'section' | 'horizon' | 'bed',
    data: null as any,
    parentIds: {} as any
  });

  const openDuplicateModal = (
    type: 'deposit' | 'section' | 'horizon' | 'bed',
    data: any,
    parentIds: any
  ) => {
    const baseName = type === 'horizon' ? data.code : data.name;
    setDuplicateName(`${baseName} (копия)`);
    setDeepCopy(false);
    setDuplicateModal({
      isOpen: true,
      type,
      data,
      parentIds
    });
  };

  const closeDuplicateModal = () => {
    //@ts-ignore
    setDuplicateModal({ isOpen: false, type: '', data: null, parentIds: {} });
    setDuplicateName('');
    setDeepCopy(false);
  };

  return {
    duplicateModal,
    duplicateName,
    deepCopy,
    setDuplicateName,
    setDeepCopy,
    openDuplicateModal,
    closeDuplicateModal
  };
};

export const useFieldManagement = () => {
    const [fields, setFields] = useState<Field[]>([]);
    const [selectedField, setSelectedField] = useState<Field | null>(null);

    // Состояния для управления видимостью на странице информации
    const [depositsVisible, setDepositsVisible] = useState<{ [key: string]: boolean }>({});
    const [sectionsVisible, setSectionsVisible] = useState<{ [key: string]: boolean }>({});
    const [horizonsVisible, setHorizonsVisible] = useState<{ [key: string]: boolean }>({});

    // Функции для управления видимостью на странице информации
    const toggleDepositVisibility = (depositId: string) => {
        setDepositsVisible(prev => ({
            ...prev,
            [depositId]: !prev[depositId]
        }));
    };

    const toggleSectionVisibility = (sectionId: string) => {
        setSectionsVisible(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const toggleHorizonVisibility = (horizonId: string) => {
        setHorizonsVisible(prev => ({
            ...prev,
            [horizonId]: !prev[horizonId]
        }));
    };

    // Функции для управления видимостью на главной странице
    const handleToggleVisibility = (fieldId: string, type: 'deposits' | 'wells') => {
        setFields(prev => prev.map(field => 
            field.id === fieldId 
                ? { ...field, depositsVisible: type === 'deposits' ? !field.depositsVisible : field.depositsVisible }
                : field
        ));
    };

    const handleToggleDepositVisibility = (fieldId: string, depositId: string) => {
        setFields(prev => prev.map(field => 
            field.id === fieldId 
                ? {
                    ...field,
                    deposits: field.deposits?.map(deposit =>
                        deposit.id === depositId
                            ? { ...deposit, sectionsVisible: !deposit.sectionsVisible }
                            : deposit
                    )
                }
                : field
        ));
    };

    const handleToggleSectionVisibility = (fieldId: string, depositId: string, sectionId: string) => {
        setFields(prev => prev.map(field => 
            field.id === fieldId 
                ? {
                    ...field,
                    deposits: field.deposits?.map(deposit =>
                        deposit.id === depositId
                            ? {
                                ...deposit,
                                sections: deposit.sections?.map(section =>
                                    section.id === sectionId
                                        ? { ...section, horizonsVisible: !section.horizonsVisible }
                                        : section
                                )
                            }
                            : deposit
                    )
                }
                : field
        ));
    };

    const handleToggleHorizonVisibility = (fieldId: string, depositId: string, sectionId: string, horizonId: string) => {
        setFields(prev => prev.map(field => 
            field.id === fieldId 
                ? {
                    ...field,
                    deposits: field?.deposits?.map(deposit =>
                        deposit.id === depositId
                            ? {
                                ...deposit,
                                sections: deposit?.sections?.map(section =>
                                    section.id === sectionId
                                        ? {
                                            ...section,
                                            horizons: section.horizons.map((horizon: any) =>
                                                horizon.id === horizonId
                                                    ? { ...horizon, bedsVisible: !horizon.bedsVisible }
                                                    : horizon
                                            )
                                        }
                                        : section
                                )
                            }
                            : deposit
                    )
                }
                : field
        ));
    };

    // Функции удаления
    const handleDeleteField = (fieldId: string) => {
        setFields(prev => prev.filter(field => field.id !== fieldId));
    };

    const handleDeleteDeposit = (fieldId: string, depositId: string) => {
        setFields(prev => prev.map(field =>
            field.id === fieldId
                ? { ...field, deposits: field?.deposits?.filter(deposit => deposit.id !== depositId) }
                : field
        ));
    };

    return {
        fields,
        setFields,
        selectedField,
        setSelectedField,
        depositsVisible,
        sectionsVisible,
        horizonsVisible,
        toggleDepositVisibility,
        toggleSectionVisibility,
        toggleHorizonVisibility,
        handleToggleVisibility,
        handleToggleDepositVisibility,
        handleToggleSectionVisibility,
        handleToggleHorizonVisibility,
        handleDeleteField,
        handleDeleteDeposit
    };
};